import { useMemo, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import type { ScheduleSlotListItem } from '../widget-api/schedule-slots';
import { formatDayRangeShort } from '../widget-lib/format-date';
import { hexToRgba, resolveSlotAccent } from '../widget-lib/slot-color';
import {
  groupTrainerSlotsBySpace,
  isTrainerOwnSlot,
  trainerSlotBookedCount,
  trainerSpaceGroupTitle
} from '../widget-lib/trainer-slot-utils';

export type TrainerStudioBranchOption = { id: number; name: string };

export type TrainerStudioSpaceFilter = number | 'none' | null;

type Props = {
  slots: ScheduleSlotListItem[];
  rangeFrom: string;
  rangeTo: string;
  selectedIso: string;
  onSelectedIsoChange: (iso: string) => void;
  me: { id?: number | null; full_name?: string | null } | null;
  branchOptions: TrainerStudioBranchOption[];
  branchFilter: number | null;
  spaceFilter: TrainerStudioSpaceFilter;
  onBranchFilterChange: (id: number | null) => void;
  onSpaceFilterChange: (filter: TrainerStudioSpaceFilter) => void;
  renderOwnBookings: (slot: ScheduleSlotListItem) => ReactNode;
  onRequestChange?: () => void;
  reload?: () => void;
};

function parseIsoToLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map((part) => Number.parseInt(part, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return new Date();
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

function toLocalIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dayKeysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  const start = parseIsoToLocalDate(from);
  const end = parseIsoToLocalDate(to);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    out.push(toLocalIsoDate(d));
  }
  return out;
}

function shortWeekday(iso: string): string {
  try {
    return parseIsoToLocalDate(iso).toLocaleDateString('ru-RU', { weekday: 'short' });
  } catch {
    return '';
  }
}

function dayNumber(iso: string): string {
  try {
    return String(parseIsoToLocalDate(iso).getDate());
  } catch {
    return '';
  }
}

function fullDayLabel(iso: string): string {
  try {
    return parseIsoToLocalDate(iso).toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });
  } catch {
    return iso;
  }
}

function spaceKeyOf(slot: ScheduleSlotListItem): number | 'none' {
  return slot.space_id ?? 'none';
}

function spaceLabelOf(slot: ScheduleSlotListItem): string {
  return slot.space_name?.trim() || 'Без пространства';
}

/**
 * Сетка тренера: дни (карусель), внутри дня — группы по пространствам.
 * Свои занятия раскрываются со списком клиентов; чужие — только «Записано N из M».
 */
export function TrainerStudioGrid({
  slots,
  rangeFrom,
  rangeTo,
  selectedIso,
  onSelectedIsoChange,
  me,
  branchOptions,
  branchFilter,
  spaceFilter,
  onBranchFilterChange,
  onSpaceFilterChange,
  renderOwnBookings,
  onRequestChange,
  reload
}: Props) {
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const multiBranch = branchOptions.length > 1;
  const branchNames = useMemo(
    () => new Map(branchOptions.map((b) => [b.id, b.name] as const)),
    [branchOptions]
  );

  const branchFiltered = useMemo(() => {
    const allowed = new Set(branchOptions.map((b) => b.id));
    return slots.filter((slot) => {
      if (branchFilter != null) return slot.branch_id === branchFilter;
      // Все филиалы = только доступные филиалы: trainer_placements тренера или все филиалы студии у админа.
      if (allowed.size === 0) return true;
      return slot.branch_id == null || allowed.has(slot.branch_id);
    });
  }, [slots, branchFilter, branchOptions]);

  const spaceOptions = useMemo(() => {
    const map = new Map<number | 'none', string>();
    for (const slot of branchFiltered) {
      const key = spaceKeyOf(slot);
      if (!map.has(key)) map.set(key, spaceLabelOf(slot));
    }
    return [...map.entries()].sort((a, b) => {
      if (a[0] === 'none') return 1;
      if (b[0] === 'none') return -1;
      return a[1].localeCompare(b[1], 'ru');
    });
  }, [branchFiltered]);

  const visibleSlots = useMemo(
    () =>
      branchFiltered.filter((slot) => {
        if (spaceFilter == null) return true;
        return spaceKeyOf(slot) === spaceFilter;
      }),
    [branchFiltered, spaceFilter]
  );

  const byDate = useMemo(() => {
    const map = new Map<string, ScheduleSlotListItem[]>();
    for (const slot of visibleSlots) {
      const list = map.get(slot.slot_date) ?? [];
      list.push(slot);
      map.set(slot.slot_date, list);
    }
    return map;
  }, [visibleSlots]);

  const dayKeys = useMemo(() => dayKeysBetween(rangeFrom, rangeTo), [rangeFrom, rangeTo]);
  const daySlots = useMemo(() => byDate.get(selectedIso) ?? [], [byDate, selectedIso]);
  const groups = useMemo(
    () => groupTrainerSlotsBySpace(daySlots, branchNames),
    [daySlots, branchNames]
  );

  function toggleExpanded(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-[color-mix(in_srgb,var(--border)_55%,transparent)] bg-[color-mix(in_srgb,var(--bg-elevated)_88%,transparent)] px-3 py-3 shadow-[var(--shadow-card)] backdrop-blur-md">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">Расписание студии</p>
        <div className="-mx-1 mt-3 flex gap-2 overflow-x-auto px-1 pb-1">
          {dayKeys.map((iso) => {
            const count = byDate.get(iso)?.length ?? 0;
            const active = iso === selectedIso;
            return (
              <button
                key={iso}
                type="button"
                onClick={() => onSelectedIsoChange(iso)}
                aria-pressed={active}
                className={`min-w-[4.5rem] rounded-2xl border px-3 py-2 text-left transition-colors ${
                  active
                    ? 'border-[color-mix(in_srgb,var(--accent)_45%,transparent)] bg-[color-mix(in_srgb,var(--accent)_16%,transparent)]'
                    : 'border-[color-mix(in_srgb,var(--border)_65%,transparent)] bg-[var(--bg-soft)]'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                    {shortWeekday(iso)}
                  </span>
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: count > 0 ? 'var(--accent)' : 'transparent' }}
                    aria-hidden
                  />
                </div>
                <div className={`mt-1 text-lg font-bold ${active ? 'text-[var(--accent)]' : 'text-[var(--text)]'}`}>
                  {dayNumber(iso)}
                </div>
              </button>
            );
          })}
        </div>

        {multiBranch || spaceOptions.length > 1 ? (
          <div className="mt-3 space-y-2">
            {multiBranch ? (
              <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1" aria-label="Фильтр по филиалу">
                <button
                  type="button"
                  onClick={() => onBranchFilterChange(null)}
                  className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold ${
                    branchFilter == null
                      ? 'border-[var(--accent)] bg-[color-mix(in_srgb,var(--accent)_14%,transparent)] text-[var(--accent)]'
                      : 'border-[var(--border)] text-[var(--text)]'
                  }`}
                >
                  Все филиалы
                </button>
                {branchOptions.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => onBranchFilterChange(b.id)}
                    className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold ${
                      branchFilter === b.id
                        ? 'border-[var(--accent)] bg-[color-mix(in_srgb,var(--accent)_14%,transparent)] text-[var(--accent)]'
                        : 'border-[var(--border)] text-[var(--text)]'
                    }`}
                  >
                    {b.name}
                  </button>
                ))}
              </div>
            ) : null}
            {spaceOptions.length > 1 ? (
              <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1" aria-label="Фильтр по пространству">
                <button
                  type="button"
                  onClick={() => onSpaceFilterChange(null)}
                  className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold ${
                    spaceFilter == null
                      ? 'border-[var(--accent)] bg-[color-mix(in_srgb,var(--accent)_14%,transparent)] text-[var(--accent)]'
                      : 'border-[var(--border)] text-[var(--text)]'
                  }`}
                >
                  Все пространства
                </button>
                {spaceOptions.map(([key, label]) => (
                  <button
                    key={String(key)}
                    type="button"
                    onClick={() => onSpaceFilterChange(key)}
                    className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold ${
                      spaceFilter === key
                        ? 'border-[var(--accent)] bg-[color-mix(in_srgb,var(--accent)_14%,transparent)] text-[var(--accent)]'
                        : 'border-[var(--border)] text-[var(--text)]'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="rounded-2xl border border-[color-mix(in_srgb,var(--border)_55%,transparent)] bg-[color-mix(in_srgb,var(--bg-elevated)_88%,transparent)] px-4 py-4 shadow-[var(--shadow-card)] backdrop-blur-md">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm text-[var(--text-muted)]">{fullDayLabel(selectedIso)}</p>
            <p className="mt-1 text-xl font-bold tracking-tight text-[var(--text)]">
              {daySlots.length === 0 ? 'Занятий нет' : `${daySlots.length} ${sessionsWord(daySlots.length)}`}
            </p>
          </div>
          {reload ? (
            <button
              type="button"
              className="shrink-0 text-xs font-semibold text-[var(--accent)] underline-offset-2 hover:underline"
              onClick={() => reload()}
            >
              Обновить
            </button>
          ) : null}
        </div>

        {groups.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-[var(--border)] bg-[var(--bg-soft)]/80 px-4 py-8 text-center text-sm text-[var(--text-muted)]">
            Нет занятий в этот день
          </div>
        ) : (
          <div className="mt-4 space-y-5">
            {groups.map((group) => (
              <section key={group.key} aria-label={trainerSpaceGroupTitle(group, multiBranch)}>
                <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                  {trainerSpaceGroupTitle(group, multiBranch)}
                </h3>
                <ul className="space-y-2">
                  {group.slots.map((slot) => {
                    const own = isTrainerOwnSlot(slot, me);
                    const isExpanded = expanded.has(slot.id);
                    const booked = trainerSlotBookedCount(slot);
                    const accentHex = resolveSlotAccent(slot.service_type_color, slot.service_color);
                    return (
                      <li
                        key={slot.id}
                        className={`overflow-hidden rounded-2xl border bg-[var(--bg-soft)] shadow-sm ${
                          own
                            ? 'border-[color-mix(in_srgb,var(--accent)_45%,transparent)]'
                            : 'border-[color-mix(in_srgb,var(--border)_55%,transparent)]'
                        }`}
                      >
                        <div className="flex">
                          <div
                            className="w-1.5 shrink-0 self-stretch"
                            style={{ backgroundColor: accentHex ? hexToRgba(accentHex, 0.85) : 'var(--accent)' }}
                            aria-hidden
                          />
                          <div className="min-w-0 flex-1 px-3 py-3">
                            <button
                              type="button"
                              className={`flex w-full items-start justify-between gap-3 text-left ${own ? 'cursor-pointer' : 'cursor-default'}`}
                              onClick={own ? () => toggleExpanded(slot.id) : undefined}
                              aria-expanded={own ? isExpanded : undefined}
                              disabled={!own}
                            >
                              <div className="min-w-0">
                                <p className="text-base font-bold text-[var(--text)]">
                                  {(slot.start_time || '').slice(0, 5)} — {(slot.end_time || '').slice(0, 5)}
                                </p>
                                <p className="mt-1 text-sm font-semibold text-[var(--text)]">
                                  {slot.service_name}
                                  {own ? (
                                    <span className="ml-2 rounded bg-[color-mix(in_srgb,var(--accent)_14%,transparent)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--accent)]">
                                      Ваше занятие
                                    </span>
                                  ) : null}
                                </p>
                                <p className="mt-0.5 text-xs text-[var(--text-muted)]">
                                  {slot.trainer_name?.trim() || 'Тренер не назначен'}
                                </p>
                              </div>
                              <div className="flex shrink-0 flex-col items-end gap-1">
                                <span className="rounded-full bg-[color-mix(in_srgb,var(--accent)_12%,transparent)] px-2.5 py-1 text-xs font-semibold text-[var(--accent)]">
                                  Записано {booked} из {slot.max_participants}
                                </span>
                                {own ? (
                                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--text-muted)]">
                                    {isExpanded ? 'Скрыть клиентов' : 'Показать клиентов'}
                                    {isExpanded ? <ChevronUp className="h-3.5 w-3.5" aria-hidden /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden />}
                                  </span>
                                ) : null}
                              </div>
                            </button>
                            {own && isExpanded ? <div className="mt-3">{renderOwnBookings(slot)}</div> : null}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}

        {onRequestChange ? (
          <button
            type="button"
            className="mt-4 w-full rounded-2xl border border-[var(--border)] py-2.5 text-sm font-medium text-[var(--text)] hover:bg-[var(--bg-soft)]"
            onClick={onRequestChange}
            title="Оформите запрос в CRM или у администратора филиала"
          >
            Запросить выходной / изменение
          </button>
        ) : null}
      </div>

      <p className="text-center text-xs text-[var(--text-muted)]">Период: {formatDayRangeShort(rangeFrom, rangeTo)}</p>
    </div>
  );
}

function sessionsWord(n: number): string {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return 'занятие';
  if (n10 >= 2 && n10 <= 4 && (n100 < 10 || n100 > 20)) return 'занятия';
  return 'занятий';
}
